/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TopBar from "../../../components/TopBar";
import UploadMediaPopup from "../../../components/UploadMediaPopup";
import useMediaUpload from "../../../hooks/useMediaUpload";
import { createBanner, updateBanner, getBanner } from "../../../api/bannerApi";

import featured from "../../../assets/images/featured.svg";
import iconDropDown from "../../../assets/images/icondropdown.svg";

import styles from './banner.module.css';

const NewBanner = () => {
    const navigate = useNavigate()
    const { bannerId } = useParams();
    const dropdownRef = useRef()

    const [bannerData, setBannerData] = useState({
        bannerTitle: '',
        bannerStyle: '',
        subtitle: '',
        desktopImage: '',
        mobileImage: '',
        title: '',
        content: '',
        buttonGroup: [
            { text: '', link: '' },
            { text: '', link: '' }
        ]
    });

    const [error, setError] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [openBannerStyleDropdown, setOpenBannerStyleDropdown] = useState(false);

    // Media popup states
    const [showUploadMediaPopup, setShowUploadMediaPopup] = useState(false);
    const [uploadMediaPopupFor, setUploadMediaPopupFor] = useState('');
    const [uploadMediaPopupTab, setUploadMediaPopupTab] = useState('upload');
    const [currentImageType, setCurrentImageType] = useState(''); // 'desktop' or 'mobile'

    // Drag and drop states
    const [isDesktopDragging, setIsDesktopDragging] = useState(false);
    const [isMobileDragging, setIsMobileDragging] = useState(false);
    const [desktopImageArray, setDesktopImageArray] = useState([]);
    const [mobileImageArray, setMobileImageArray] = useState([]);

    const fileList = {
        'image/jpeg': true,
        'image/png': true,
        'image/jpg': true
    };

    const { handleMeidaUpload: handleDesktopUpload } = useMediaUpload(setDesktopImageArray, null, fileList);
    const { handleMeidaUpload: handleMobileUpload } = useMediaUpload(setMobileImageArray, null, fileList);

    const fetchBanner = async () => {
        try {
            const response = await getBanner(bannerId);
            if (response?.success) {
                setBannerData({
                    ...response.data,
                    bannerStyle: response?.data?.bannerStyle || '',
                    buttonGroup: [
                        { text: response?.data?.buttonGroup?.[0]?.text || '', link: response?.data?.buttonGroup?.[0]?.link || '' },
                        { text: response?.data?.buttonGroup?.[1]?.text || '', link: response?.data?.buttonGroup?.[1]?.link || '' }
                    ]
                });
            }
        } catch (error) {
            console.log('Error fetching banner:', error);
        }
    };

    useEffect(() => {
        if (bannerId) {
            fetchBanner();
        }
    }, [bannerId]);

    useEffect(() => {
        if (desktopImageArray.length > 0) {
            setBannerData(prev => ({ ...prev, desktopImage: desktopImageArray[0]?.id }));
            setError({ ...error, desktopImage: '' });
        } else if (bannerData.desktopImage && !desktopImageArray.length) {
            // If editing and has image, set array
            setDesktopImageArray([bannerData.desktopImage]);
        }
    }, [desktopImageArray, bannerData.desktopImage]);

    useEffect(() => {
        if (mobileImageArray.length > 0) {
            setBannerData(prev => ({ ...prev, mobileImage: mobileImageArray[0]?.id }));
        } else if (bannerData.mobileImage && !mobileImageArray.length) {
            setMobileImageArray([bannerData.mobileImage]);
        }
    }, [mobileImageArray, bannerData.mobileImage]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenBannerStyleDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const validate = () => {
        let newErrors = {};

         if (!bannerData.bannerTitle.trim()) {
            newErrors.bannerTitle = "Banner Title is required";
        }

        if (!bannerData.bannerStyle) {
            newErrors.bannerStyle = "Banner Style is required";
        }

        if (bannerData.bannerStyle === 'BANNER_STYLE_2' && !bannerData.subtitle?.trim()) {
            newErrors.subtitle = "Subtitle is required for Banner Style 2";
        }

        if (bannerData?.desktopImage === '') {
            newErrors.desktopImage = "Desktop Image is required";
        }

        setError(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        try {
            setIsLoading(true);
            const payload = {
                ...bannerData,
                buttonGroup: bannerData.buttonGroup.filter(btn => btn.text.trim() || btn.link.trim())
            };

            if (bannerId) {
                await updateBanner(bannerId, payload);

            } else {
                const response = await createBanner(payload);
                if (response?.success) {
                    const responseUpdate = await updateBanner(response?.data?.bannerId, payload);
                    if (responseUpdate?.success) {
                        navigate(`/appearance/banner/${response?.data?.bannerId}`);
                    }
                }
            }
            setIsLoading(false);
        } catch (error) {
            if(error?.response?.data?.error?.message === "Banner title already exists"){
                setError({...error, bannerTitle: error?.response?.data?.error?.message })
            }
            console.log('Error saving banner:', error);
            setIsLoading(false);
        }
    };

    const handleImageSelect = (image) => {
        if (currentImageType === 'desktop') {
            setBannerData({ ...bannerData, desktopImage: image });
            setError({ ...error, desktopImage: '' });
            setDesktopImageArray(image);
        } else if (currentImageType === 'mobile') {
            setBannerData({ ...bannerData, mobileImage: image });
            setMobileImageArray(image);
        }
        setShowUploadMediaPopup(false);
    };

    const handleFileDragOver = (e, type) => {
        e.preventDefault();
        if (type === 'desktop' && !isDesktopDragging) {
            setIsDesktopDragging(true);
        } else if (type === 'mobile' && !isMobileDragging) {
            setIsMobileDragging(true);
        }
    };

    const handleFileDragLeave = (e, type) => {
        e.preventDefault();
        if (type === 'desktop') {
            setIsDesktopDragging(false);
        } else if (type === 'mobile') {
            setIsMobileDragging(false);
        }
    };

    const handleDesktopDrop = async (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        const maxSizeInBytes = 128 * 1024 * 1024; // 128MB
        const allowedFileTypes = ['image/jpeg', 'image/png', 'image/jpg'];

        const validFiles = Array.from(files).filter(file => {
            if (!allowedFileTypes.includes(file.type)) {
                console.log(`File ${file.name} is not an allowed type. Only JPG, JPEG, and PNG are allowed.`);
                return false;
            }
            if (file.size > maxSizeInBytes) {
                console.log(`File ${file.name} exceeds the maximum size of 128MB.`);
                return false;
            }
            return true;
        }).slice(0, 1);

        handleDesktopUpload(validFiles);
        setIsDesktopDragging(false);
    };

    const handleMobileDrop = async (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        const maxSizeInBytes = 128 * 1024 * 1024; // 128MB
        const allowedFileTypes = ['image/jpeg', 'image/png', 'image/jpg'];

        const validFiles = Array.from(files).filter(file => {
            if (!allowedFileTypes.includes(file.type)) {
                console.log(`File ${file.name} is not an allowed type. Only JPG, JPEG, and PNG are allowed.`);
                return false;
            }
            if (file.size > maxSizeInBytes) {
                console.log(`File ${file.name} exceeds the maximum size of 128MB.`);
                return false;
            }
            return true;
        }).slice(0, 1);

        handleMobileUpload(validFiles);
        setIsMobileDragging(false);
    };

    const handleButtonChange = (index, field, value) => {
        const newButtonGroup = [...bannerData.buttonGroup];
        newButtonGroup[index][field] = value;
        setBannerData({ ...bannerData, buttonGroup: newButtonGroup });
    };

    return (
        <>
            <TopBar />
            <div className={styles.addBannerContainer}>
                <div className={styles.bannerForm}>

                    <div className={styles.inputField}>
                        <label>Banner Title</label>
                        <input
                            type="text"
                            className={styles.bannerInput}
                            value={bannerData.bannerTitle}
                            disabled={bannerData?.bannerTitle === 'Main banner'}
                            onChange={(e) => {
                                setBannerData({ ...bannerData, bannerTitle: e.target.value });
                                setError({ ...error, bannerTitle: '' });
                            }}
                            placeholder="Enter banner title"
                        />
                        {error.bannerTitle && <span className="error-message">{error.bannerTitle}</span>}
                    </div>
                    <div className={styles.inputField} ref={dropdownRef}>
                        <label>Banner Style</label>
                        <div className="dropdown-wrapper" >
                            <div className="dropdown-header" onClick={() => setOpenBannerStyleDropdown(!openBannerStyleDropdown)}>
                                <span className="title">{ bannerData?.bannerStyle === 'BANNER_STYLE_1' ? 'Banner Style 1' : bannerData?.bannerStyle === 'BANNER_STYLE_2' ? 'Banner Style 2' : bannerData?.bannerStyle === 'BANNER_STYLE_3' ? 'Banner Style 3' : "Select Banner Style"}</span>
                                <span className={`arrow ${openBannerStyleDropdown ? "rotate" : ""}`}><img src={iconDropDown} alt='icon-dropdown' /></span>
                            </div>
                            {openBannerStyleDropdown && (
                                <div className="dropdown-list">
                                    <label
                                        className="dropdown-item"
                                        onClick={() => {
                                            setBannerData({ ...bannerData, bannerStyle: 'BANNER_STYLE_1' });
                                            setError({ ...error, bannerStyle: '' });
                                            setOpenBannerStyleDropdown(false);
                                        }}
                                    >
                                        <span>Banner Style 1</span>
                                    </label>
                                    <label
                                        className="dropdown-item"
                                        onClick={() => {
                                            setBannerData({ ...bannerData, bannerStyle: 'BANNER_STYLE_2' });
                                            setError({ ...error, bannerStyle: '' });
                                            setOpenBannerStyleDropdown(false);
                                        }}
                                    >
                                        <span>Banner Style 2</span>
                                    </label>
                                    <label
                                        className="dropdown-item"
                                        onClick={() => {
                                            setBannerData({ ...bannerData, bannerStyle: 'BANNER_STYLE_3' });
                                            setError({ ...error, bannerStyle: '' });
                                            setOpenBannerStyleDropdown(false);
                                        }}
                                    >
                                        <span>Banner Style 3</span>
                                    </label>
                                </div>
                            )}
                        </div>
                        {error.bannerStyle && <span className="error-message">{error.bannerStyle}</span>}
                    </div>

                    <div className={styles.inputField}>
                        <label>Title</label>
                        <input
                            type="text"
                            className={styles.bannerInput}
                            value={bannerData.title}
                            onChange={(e) => setBannerData({ ...bannerData, title: e.target.value })}
                            placeholder="Enter title"
                            />
                    </div>

                    {bannerData.bannerStyle === 'BANNER_STYLE_2' && (
                        <div className={styles.inputField}>
                            <label>Subtitle</label>
                            <input
                                type="text"
                                className={styles.bannerInput}
                                value={bannerData.subtitle}
                                onChange={(e) => {
                                    setBannerData({ ...bannerData, subtitle: e.target.value });
                                    setError({ ...error, subtitle: '' });
                                }}
                                placeholder="Enter subtitle"
                            />
                            {error.subtitle && <span className="error-message">{error.subtitle}</span>}
                        </div>
                    )}

                    <div className={styles.inputField}>
                        <label>Content</label>
                        <input
                            type="text"
                            className={styles.bannerInput}
                            value={bannerData.content}
                            onChange={(e) => setBannerData({ ...bannerData, content: e.target.value })}
                            placeholder="Enter content "
                        />
                    </div>

                    <div className={styles.buttonGroup}>
                        <label>Button Group</label>
                        {bannerData?.buttonGroup?.map((button, index) => (
                            <div key={index} className={styles.buttonField}>
                                <input
                                    type="text"
                                    placeholder={`Button ${index + 1} Text`}
                                    value={button.text}
                                    onChange={(e) => handleButtonChange(index, 'text', e.target.value)}
                                    className={styles.bannerInput}
                                />
                                <input
                                    type="text"
                                    placeholder={`Button ${index + 1} Link`}
                                    value={button.link}
                                    onChange={(e) => handleButtonChange(index, 'link', e.target.value)}
                                    className={styles.bannerInput}
                                />
                            </div>
                        ))}
                    </div>

                    <div className={styles.imageGroup}>
                        <div className={styles.inputField}>
                            <label>Desktop Image </label>
                            <div className={`${styles.imageUploadBox} ${isDesktopDragging ? styles.draging : ''}`}
                                onDragOver={(e) => handleFileDragOver(e, 'desktop')}
                                onDragLeave={(e) => handleFileDragLeave(e, 'desktop')}
                                onDrop={handleDesktopDrop}
                                onClick={(e) => { e.stopPropagation(); setCurrentImageType('desktop'); setShowUploadMediaPopup(true); setUploadMediaPopupFor('feature-image'); setUploadMediaPopupTab('media'); }}>
                                {(desktopImageArray[0] || bannerData.desktopImage) ? (
                                    desktopImageArray[0]?.isLoading ? (
                                        <div className={styles.progressWrap}>
                                            <div className={styles.progress}>
                                                <div className={styles.color}></div>
                                            </div>
                                        </div>
                                    ) : (
                                        <img src={(desktopImageArray[0]?.url || bannerData.desktopImage)} className={`${styles.featuredImg}`} alt="Desktop" />
                                    )
                                ) : (
                                    <>
                                        <div id="desktopImageUpload" className={`${styles.newpageImageUpload}`}>
                                            <div className={`${styles.featureImgMiddleContent}`}>
                                                <img className={`${styles.fileUploadCover_Img}`} src={featured} alt="FeaturedImage" />
                                                <p className={`${styles.newPageImageUploadText}`}>
                                                    <span onClick={(e) => { e.stopPropagation(); setCurrentImageType('desktop'); setShowUploadMediaPopup(true); setUploadMediaPopupFor('feature-image'); setUploadMediaPopupTab('media'); }}>Upload</span> or <span onClick={(e) => { e.stopPropagation(); setCurrentImageType('desktop'); setShowUploadMediaPopup(true); setUploadMediaPopupFor('feature-image'); setUploadMediaPopupTab('media'); }}>Open Media Library</span>
                                                    <br />
                                                    JPG, PNG (max. 128MB)
                                                </p>
                                            </div>
                                        </div>
                                        <input type="file" id="desktop-img" accept="image/*" hidden />
                                    </>
                                )}
                            </div>
                            {error.desktopImage && <span className="error-message">{error.desktopImage}</span>}
                        </div>

                        <div className={styles.inputField}>
                            <label>Mobile Image</label>
                            <div className={`${styles.imageUploadBox} ${isMobileDragging ? styles.draging : ''}`}
                                onDragOver={(e) => handleFileDragOver(e, 'mobile')}
                                onDragLeave={(e) => handleFileDragLeave(e, 'mobile')}
                                onDrop={handleMobileDrop}
                                onClick={(e) => { e.stopPropagation(); setCurrentImageType('mobile'); setShowUploadMediaPopup(true); setUploadMediaPopupFor('feature-image'); setUploadMediaPopupTab('media'); }}>
                                {(mobileImageArray[0] || bannerData.mobileImage) ? (
                                    mobileImageArray[0]?.isLoading ? (
                                        <div className={styles.progressWrap}>
                                            <div className={styles.progress}>
                                                <div className={styles.color}></div>
                                            </div>
                                        </div>
                                    ) : (
                                        <img src={(mobileImageArray[0]?.url || bannerData.mobileImage)} className={`${styles.featuredImg}`} alt="Mobile" />
                                    )
                                ) : (
                                    <>
                                        <div id="mobileImageUpload" className={`${styles.newpageImageUpload}`}>
                                            <div className={`${styles.featureImgMiddleContent}`}>
                                                <img className={`${styles.fileUploadCover_Img}`} src={featured} alt="FeaturedImage" />
                                                <p className={`${styles.newPageImageUploadText}`}>
                                                    <span onClick={(e) => { e.stopPropagation(); setCurrentImageType('mobile'); setShowUploadMediaPopup(true); setUploadMediaPopupFor('feature-image'); setUploadMediaPopupTab('media'); }}>Upload</span> or <span onClick={(e) => { e.stopPropagation(); setCurrentImageType('mobile'); setShowUploadMediaPopup(true); setUploadMediaPopupFor('feature-image'); setUploadMediaPopupTab('media'); }}>Open Media Library</span>
                                                    <br />
                                                    JPG, PNG (max. 128MB)
                                                </p>
                                            </div>
                                        </div>
                                        <input type="file" id="mobile-img" accept="image/*" hidden />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={`save-btn-group ${styles.saveButton}`}>
                        <button onClick={handleSave} className='save-btn' disabled={isLoading}>
                            {isLoading ? <div className={`ringLoader loaderContinue`}></div> : 'Save'}
                        </button>
                    </div>
                </div>
            </div>

            {showUploadMediaPopup && (
                <UploadMediaPopup
                    showUploadMediaPopup={showUploadMediaPopup}
                    setShowUploadMediaPopup={setShowUploadMediaPopup}
                    setSelectedProfileImage={handleImageSelect}
                    uploadMediaPopupFor={uploadMediaPopupFor}
                    uploadMediaPopupTab={uploadMediaPopupTab}
                />
            )}
        </>
    );
};

export default NewBanner;